import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import api from '../../lib/api';
import { 
  MessageCircle, 
  Send, 
  Inbox,
  Building2,
  Clock,
  Loader2,
  Circle,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';

export default function SupportInbox() {
  const { user } = useAuth();
  const { lastMessage } = useWebSocket();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const selectedThreadRef = useRef(null);
  selectedThreadRef.current = selectedThread;

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedThread?.messages]);

  const fetchThreads = useCallback(async () => {
    try {
      const response = await api.get('/support/threads');
      setThreads(response.data.threads || []);
    } catch (error) {
      toast.error('Failed to load support threads');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadThread = useCallback(async (institutionId) => {
    try {
      const response = await api.get('/support/thread', {
        params: { institution_id: institutionId }
      });
      setSelectedThread(response.data);
      
      // Mark as read
      await api.post('/support/mark-read', { institution_id: institutionId });
      
      // Update local threads to reset unread count
      setThreads(prev => prev.map(t => 
        t.institution_id === institutionId 
          ? { ...t, unread_by_platform_admin: 0 } 
          : t
      ));
    } catch (error) {
      toast.error('Failed to load thread');
    }
  }, []);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Listen for new support messages
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'support.new_message_platform') {
      fetchThreads();
      const current = selectedThreadRef.current;
      if (current && current.institution_id === lastMessage.data.institution_id) {
        loadThread(current.institution_id);
      }
    }
  }, [lastMessage, fetchThreads, loadThread]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedThread) {
      return;
    }
    
    setSending(true);
    try {
      await api.post('/support/send-message', {
        message: newMessage.trim(),
        institution_id: selectedThread.institution_id
      });
      
      setNewMessage('');
      // Reload thread
      await loadThread(selectedThread.institution_id);
      await fetchThreads();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  if (user?.institution_id) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <MessageCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Platform Administrator Only</h2>
            <p className="text-slate-500">
              Support Inbox is only accessible to platform administrators.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Inbox className="w-6 h-6 text-red-500" />
          Customer Support Inbox
        </h1>
        <p className="text-slate-500 mt-1">Manage support requests from institutions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
        {/* Thread List */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="text-lg">Conversations ({threads.length})</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
              </div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center">
                <MessageCircle className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500 text-sm">No support threads yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {threads.map((thread) => (
                  <button
                    key={thread.institution_id}
                    onClick={() => loadThread(thread.institution_id)}
                    className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                      selectedThread?.institution_id === thread.institution_id
                        ? 'bg-red-50 border-l-4 border-l-red-500'
                        : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900 text-sm">
                          {thread.institution_name}
                        </span>
                      </div>
                      {thread.unread_by_platform_admin > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {thread.unread_by_platform_admin}
                        </span>
                      )}
                    </div>
                    
                    {thread.messages && thread.messages.length > 0 && (
                      <>
                        <p className="text-xs text-slate-600 line-clamp-2">
                          {thread.messages[thread.messages.length - 1].message_text}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(thread.last_message_at)}
                        </div>
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="lg:col-span-2 flex flex-col">
          {selectedThread ? (
            <>
              <CardHeader className="flex-shrink-0 border-b">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="w-5 h-5 text-red-500" />
                  {selectedThread.institution_name}
                </CardTitle>
                <CardDescription>
                  {selectedThread.messages?.length || 0} messages
                </CardDescription>
              </CardHeader>

              <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
                {selectedThread.messages && selectedThread.messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-slate-400">No messages yet</p>
                  </div>
                ) : (
                  selectedThread.messages?.map((msg) => {
                    const isPlatformAdmin = msg.sender_institution_id === null;
                    const isOwnMessage = msg.sender_user_id === user.id;
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            isOwnMessage
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-100 text-slate-900'
                          }`}
                        >
                          {!isOwnMessage && (
                            <p className="text-xs font-medium mb-1 opacity-80">
                              {msg.sender_name}
                              <span className="ml-2 text-xs opacity-60">
                                ({msg.sender_role})
                              </span>
                            </p>
                          )}
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">
                            {msg.message_text}
                          </p>
                          <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/70' : 'text-slate-500'}`}>
                            {formatTimestamp(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </CardContent>

              <div className="border-t p-4 bg-slate-50 flex-shrink-0">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your reply..."
                    disabled={sending}
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <CardContent className="flex items-center justify-center h-full">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">Select a conversation</p>
                <p className="text-sm text-slate-400 mt-1">
                  Choose an institution from the list to view messages
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
